/**
 * AnimatedBackground - Connected World Network with Paradise Elements
 * 
 * A premium animated background for Freestays featuring:
 * - Floating nodes representing destinations
 * - Connecting lines (travel routes)
 * - Occasional flight arc animations
 * - Mouse parallax effect
 * - Soft floating particles (paradise elements)
 * 
 * Brand colors: Blue (#1e5bb8) + Teal (#0d9488) + Gold accents
 */

import { useEffect, useRef, useCallback } from 'react';

const AnimatedBackground = ({ 
  variant = 'full', // 'full' for homepage, 'hero' for hero sections only
  className = '',
  opacity = 1 
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const nodesRef = useRef([]);
  const particlesRef = useRef([]);
  const flightArcsRef = useRef([]);
  const lastTimeRef = useRef(0);

  // Configuration
  const CONFIG = {
    // Network nodes (destinations)
    nodeCount: typeof window !== 'undefined' && window.innerWidth < 768 ? 25 : 45,
    maxLinkDist: typeof window !== 'undefined' && window.innerWidth < 768 ? 120 : 160,
    nodeSpeed: 0.15, // Slow & relaxing
    nodeMinR: 1.5,
    nodeMaxR: 3,
    
    // Paradise particles
    particleCount: typeof window !== 'undefined' && window.innerWidth < 768 ? 15 : 30,
    particleSpeed: 0.08,
    particleMinR: 0.8,
    particleMaxR: 2.5,
    
    // Flight arcs
    arcSpawnInterval: 4000, // New arc every 4 seconds
    arcDuration: 3000,
    maxArcs: 3,
    
    // Visual
    parallax: 15,
    fpsCap: 60,
    
    // Brand colors (Freestays) - Adjusted for light background visibility
    colors: {
      primary: { r: 30, g: 91, b: 184 },      // Blue #1e5bb8
      accent: { r: 13, g: 148, b: 136 },       // Teal #0d9488
      gold: { r: 220, g: 160, b: 50 },         // Gold accent (darker for visibility)
      white: { r: 80, g: 120, b: 180 }         // Blue-tinted for light backgrounds
    }
  };

  const rand = (min, max) => min + Math.random() * (max - min);

  const initNodes = useCallback((width, height) => {
    const nodes = [];
    for (let i = 0; i < CONFIG.nodeCount; i++) {
      // Determine color: mostly blue/teal, occasional gold
      const colorChoice = Math.random();
      let color;
      if (colorChoice < 0.5) {
        color = CONFIG.colors.primary;
      } else if (colorChoice < 0.9) {
        color = CONFIG.colors.accent;
      } else {
        color = CONFIG.colors.gold;
      }
      
      nodes.push({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-1, 1) * CONFIG.nodeSpeed,
        vy: rand(-1, 1) * CONFIG.nodeSpeed,
        r: rand(CONFIG.nodeMinR, CONFIG.nodeMaxR),
        glow: rand(0.1, 0.25),
        color,
        pulse: rand(0, Math.PI * 2), // For subtle pulsing
        pulseSpeed: rand(0.01, 0.03)
      });
    }
    return nodes;
  }, []);

  const initParticles = useCallback((width, height) => {
    const particles = [];
    for (let i = 0; i < CONFIG.particleCount; i++) {
      particles.push({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-0.5, 0.5) * CONFIG.particleSpeed,
        vy: rand(-0.3, -0.1) * CONFIG.particleSpeed, // Gentle upward drift
        r: rand(CONFIG.particleMinR, CONFIG.particleMaxR),
        opacity: rand(0.1, 0.4),
        color: Math.random() > 0.7 ? CONFIG.colors.gold : CONFIG.colors.white
      });
    }
    return particles;
  }, []);

  const spawnFlightArc = useCallback((width, height) => {
    if (flightArcsRef.current.length >= CONFIG.maxArcs) return;
    
    // Pick two random positions (start and end)
    const startX = rand(width * 0.1, width * 0.9);
    const startY = rand(height * 0.2, height * 0.8);
    const endX = rand(width * 0.1, width * 0.9);
    const endY = rand(height * 0.2, height * 0.8);
    
    // Control point for the arc (creates the curve)
    const midX = (startX + endX) / 2;
    const midY = Math.min(startY, endY) - rand(50, 150); // Arc upward
    
    flightArcsRef.current.push({
      startX, startY,
      endX, endY,
      midX, midY,
      progress: 0,
      startTime: Date.now(),
      duration: CONFIG.arcDuration,
      color: Math.random() > 0.5 ? CONFIG.colors.primary : CONFIG.colors.accent
    });
  }, []);

  const draw = useCallback((ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    
    const mx = mouseRef.current.active ? mouseRef.current.x : 0.5;
    const my = mouseRef.current.active ? mouseRef.current.y : 0.5;
    const px = (mx - 0.5) * CONFIG.parallax;
    const py = (my - 0.5) * CONFIG.parallax;
    
    const nodes = nodesRef.current;
    const particles = particlesRef.current;
    const arcs = flightArcsRef.current;
    
    // Update and draw flight arcs first (behind everything)
    const now = Date.now();
    for (let i = arcs.length - 1; i >= 0; i--) {
      const arc = arcs[i];
      const elapsed = now - arc.startTime;
      arc.progress = Math.min(1, elapsed / arc.duration);
      
      if (arc.progress >= 1) {
        arcs.splice(i, 1);
        continue;
      }
      
      // Draw arc trail
      const steps = Math.floor(arc.progress * 50);
      for (let s = 0; s < steps; s++) {
        const t = s / 50;
        const fadeT = Math.max(0, (arc.progress - t) * 3);
        if (fadeT <= 0) continue;
        
        // Quadratic bezier
        const x = (1-t)*(1-t)*arc.startX + 2*(1-t)*t*arc.midX + t*t*arc.endX;
        const y = (1-t)*(1-t)*arc.startY + 2*(1-t)*t*arc.midY + t*t*arc.endY;
        
        ctx.beginPath();
        ctx.arc(x + px, y + py, 1.5 * fadeT, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${arc.color.r}, ${arc.color.g}, ${arc.color.b}, ${0.4 * fadeT})`;
        ctx.fill();
      }
      
      // Draw plane head
      const headT = arc.progress;
      const headX = (1-headT)*(1-headT)*arc.startX + 2*(1-headT)*headT*arc.midX + headT*headT*arc.endX;
      const headY = (1-headT)*(1-headT)*arc.startY + 2*(1-headT)*headT*arc.midY + headT*headT*arc.endY;
      
      // Glow
      ctx.beginPath();
      ctx.arc(headX + px, headY + py, 6, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${arc.color.r}, ${arc.color.g}, ${arc.color.b}, 0.3)`;
      ctx.fill();
      
      // Core
      ctx.beginPath();
      ctx.arc(headX + px, headY + py, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, 0.9)`;
      ctx.fill();
    }
    
    // Draw connecting lines between nodes
    const maxD = CONFIG.maxLinkDist;
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i];
      
      // Update position
      a.x += a.vx;
      a.y += a.vy;
      a.pulse += a.pulseSpeed;
      
      // Wrap edges
      if (a.x < -30) a.x = width + 30;
      if (a.x > width + 30) a.x = -30;
      if (a.y < -30) a.y = height + 30;
      if (a.y > height + 30) a.y = -30;
      
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.hypot(dx, dy);
        if (dist > maxD) continue;
        
        const t = 1 - dist / maxD;
        
        // Gradient line color based on node colors
        const gradient = ctx.createLinearGradient(
          a.x + px, a.y + py, 
          b.x + px, b.y + py
        );
        gradient.addColorStop(0, `rgba(${a.color.r}, ${a.color.g}, ${a.color.b}, ${0.35 * t})`);
        gradient.addColorStop(1, `rgba(${b.color.r}, ${b.color.g}, ${b.color.b}, ${0.35 * t})`);
        
        ctx.beginPath();
        ctx.moveTo(a.x + px, a.y + py);
        ctx.lineTo(b.x + px, b.y + py);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    }
    
    // Draw nodes
    for (const n of nodes) {
      const pulseScale = 1 + Math.sin(n.pulse) * 0.15;
      
      // Outer glow
      ctx.beginPath();
      ctx.arc(n.x + px, n.y + py, n.r * 4.5 * pulseScale, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${n.color.r}, ${n.color.g}, ${n.color.b}, ${n.glow * 0.6})`;
      ctx.fill();
      
      // Inner glow
      ctx.beginPath();
      ctx.arc(n.x + px, n.y + py, n.r * 2.5 * pulseScale, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${n.color.r}, ${n.color.g}, ${n.color.b}, ${n.glow * 1.2})`;
      ctx.fill();
      
      // Core - use a brighter version of the node color
      ctx.beginPath();
      ctx.arc(n.x + px, n.y + py, n.r * 1.2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${Math.min(255, n.color.r + 60)}, ${Math.min(255, n.color.g + 60)}, ${Math.min(255, n.color.b + 60)}, 0.95)`;
      ctx.fill();
    }
    
    // Draw floating particles (paradise elements)
    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      
      // Wrap with fade at edges
      if (p.y < -20) p.y = height + 20;
      if (p.x < -20) p.x = width + 20;
      if (p.x > width + 20) p.x = -20;
      
      // Soft bokeh-like particle
      ctx.beginPath();
      ctx.arc(p.x + px * 0.5, p.y + py * 0.5, p.r * 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.opacity * 0.3})`;
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(p.x + px * 0.5, p.y + py * 0.5, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.opacity})`;
      ctx.fill();
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    // Check for reduced motion preference
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduceMotion) {
      // Show static gradient instead
      container.style.background = `
        radial-gradient(ellipse 1200px 800px at 20% 10%, rgba(30, 91, 184, 0.15), transparent 60%),
        radial-gradient(ellipse 900px 700px at 80% 30%, rgba(13, 148, 136, 0.12), transparent 55%),
        radial-gradient(ellipse 800px 600px at 50% 85%, rgba(255, 200, 87, 0.08), transparent 60%)
      `;
      return;
    }

    const ctx = canvas.getContext('2d', { alpha: true });
    let width = 0;
    let height = 0;
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    const resize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      // Reinitialize elements
      nodesRef.current = initNodes(width, height);
      particlesRef.current = initParticles(width, height);
      flightArcsRef.current = [];
    };

    const handleMouseMove = (e) => {
      mouseRef.current.active = true;
      mouseRef.current.x = e.clientX / Math.max(1, width);
      mouseRef.current.y = e.clientY / Math.max(1, height);
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    // Animation loop with FPS cap
    let arcSpawnTimer = 0;
    const animate = (timestamp) => {
      const delta = timestamp - lastTimeRef.current;
      const minDelta = 1000 / CONFIG.fpsCap;
      
      if (delta >= minDelta) {
        lastTimeRef.current = timestamp;
        draw(ctx, width, height);
        
        // Spawn flight arcs periodically
        arcSpawnTimer += delta;
        if (arcSpawnTimer >= CONFIG.arcSpawnInterval) {
          arcSpawnTimer = 0;
          spawnFlightArc(width, height);
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Initialize
    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    
    // Start animation
    animationRef.current = requestAnimationFrame(animate);
    
    // Spawn initial arc
    setTimeout(() => spawnFlightArc(width, height), 1000);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initNodes, initParticles, draw, spawnFlightArc]);

  return (
    <div 
      ref={containerRef}
      className={`animated-background ${className}`}
      style={{
        position: variant === 'full' ? 'fixed' : 'absolute',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        opacity,
        background: `
          radial-gradient(ellipse 1200px 800px at 20% 10%, rgba(30, 91, 184, 0.12), transparent 60%),
          radial-gradient(ellipse 900px 700px at 80% 30%, rgba(13, 148, 136, 0.10), transparent 55%),
          radial-gradient(ellipse 800px 600px at 50% 85%, rgba(255, 200, 87, 0.06), transparent 60%),
          linear-gradient(180deg, rgba(240, 245, 255, 0.95) 0%, rgba(248, 250, 255, 0.9) 50%, rgba(245, 248, 255, 0.95) 100%)
        `
      }}
    >
      {/* Option D: Flowing Gradient Waves - Sophisticated & Calming */}
      <div className="flowing-gradients" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {/* Primary Blue Blob */}
        <div 
          style={{
            position: 'absolute',
            width: '60vw',
            height: '60vw',
            maxWidth: '800px',
            maxHeight: '800px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(30, 91, 184, 0.25) 0%, rgba(30, 91, 184, 0.08) 50%, transparent 70%)',
            filter: 'blur(60px)',
            top: '-10%',
            left: '-10%',
            animation: 'floatBlob1 25s ease-in-out infinite',
          }}
        />
        {/* Teal/Emerald Blob */}
        <div 
          style={{
            position: 'absolute',
            width: '50vw',
            height: '50vw',
            maxWidth: '700px',
            maxHeight: '700px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(13, 148, 136, 0.22) 0%, rgba(13, 148, 136, 0.06) 50%, transparent 70%)',
            filter: 'blur(50px)',
            top: '30%',
            right: '-15%',
            animation: 'floatBlob2 30s ease-in-out infinite',
          }}
        />
        {/* Gold/Warm Blob */}
        <div 
          style={{
            position: 'absolute',
            width: '45vw',
            height: '45vw',
            maxWidth: '600px',
            maxHeight: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 180, 50, 0.15) 0%, rgba(255, 180, 50, 0.04) 50%, transparent 70%)',
            filter: 'blur(55px)',
            bottom: '-5%',
            left: '20%',
            animation: 'floatBlob3 28s ease-in-out infinite',
          }}
        />
        {/* Secondary Blue Blob (smaller) */}
        <div 
          style={{
            position: 'absolute',
            width: '35vw',
            height: '35vw',
            maxWidth: '500px',
            maxHeight: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.05) 50%, transparent 70%)',
            filter: 'blur(45px)',
            top: '60%',
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'floatBlob4 22s ease-in-out infinite',
          }}
        />
      </div>
      
      {/* CSS Keyframes for flowing animation */}
      <style>{`
        @keyframes floatBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(10%, 15%) scale(1.05); }
          50% { transform: translate(5%, 10%) scale(0.95); }
          75% { transform: translate(-5%, 5%) scale(1.02); }
        }
        @keyframes floatBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-15%, 10%) scale(1.08); }
          66% { transform: translate(-10%, -5%) scale(0.92); }
        }
        @keyframes floatBlob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(15%, -10%) scale(1.03); }
          40% { transform: translate(20%, 5%) scale(0.97); }
          60% { transform: translate(5%, 10%) scale(1.05); }
          80% { transform: translate(-10%, 5%) scale(0.98); }
        }
        @keyframes floatBlob4 {
          0%, 100% { transform: translateX(-50%) scale(1); }
          50% { transform: translateX(-50%) translate(10%, -15%) scale(1.1); }
        }
      `}</style>
      
      {/* Network Animation Canvas (nodes, connections, flight arcs) */}
      <canvas 
        ref={canvasRef} 
        style={{ 
          position: 'absolute', 
          inset: 0,
          opacity: 0.85
        }} 
      />
      
      {/* Subtle light overlay for depth and glass effect */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 25%, transparent 75%, rgba(255,255,255,0.15) 100%)',
          pointerEvents: 'none'
        }}
      />
    </div>
  );
};

export default AnimatedBackground;
